import type { ObjectDirective } from "vue";

/**
 * Apple-like motion primitives. Numbers match APPLE-MOTION.md:
 *   200ms + cubic-bezier(0.16, 1, 0.3, 1)  — menus / nudges
 *   350ms + cubic-bezier(0.25, 0.1, 0.25, 1) — layout
 *   500/400ms + cubic-bezier(0.32, 0.72, 0, 1) — sheets (in longer than out)
 */

export const APPLE_SHEET_LEAVE_GHOST_CLASS = "apple-sheet-leave-ghost";
export const APPLE_DIALOG_LEAVE_GHOST_CLASS = "apple-dialog-leave-ghost";
export const APPLE_LAYOUT_FLIP_CLASS = "apple-layout-flip-active";

/** Matches Tailwind `backdrop-blur-sm` on the fullscreen overlay. */
const DIALOG_BACKDROP_BLUR_PX = 4;
/** `black.opaced` in style.json: #00000033. */
const DIALOG_BACKDROP_DIM_ALPHA = 0x33 / 255;

export type AppleMotionKind = "fast" | "layout" | "sheet" | "sheet-out";

export type ViewBoxLike = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

const DURATION_MS: Record<AppleMotionKind, number> = {
  fast: 200,
  layout: 350,
  sheet: 500,
  "sheet-out": 400,
};

export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function appleDurationMs(kind: AppleMotionKind): number {
  if (prefersReducedMotion()) return 0;
  return DURATION_MS[kind];
}

function bezier1d(t: number, a1: number, a2: number): number {
  // (1-t)^3 * 0 + 3(1-t)^2 t * a1 + 3(1-t) t^2 * a2 + t^3 * 1
  const u = 1 - t;
  return 3 * u * u * t * a1 + 3 * u * t * t * a2 + t * t * t;
}

/** Unit cubic-bezier: maps linear time in [0, 1] to eased progress. */
export function cubicBezierEase(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): (t: number) => number {
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let lo = 0;
    let hi = 1;
    let t = x;
    for (let i = 0; i < 16; i++) {
      const currentX = bezier1d(t, x1, x2);
      if (Math.abs(currentX - x) < 1e-6) break;
      if (currentX < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return bezier1d(t, y1, y2);
  };
}

/** CAMediaTimingFunction.default / UIView.animate */
export const easeApple = cubicBezierEase(0.25, 0.1, 0.25, 1);
/** Menus, tooltips, zoom nudges — strong ease-out. */
export const easeAppleOut = cubicBezierEase(0.16, 1, 0.3, 1);
/** UISheetPresentationController: almost-linear start, heavy settle. */
export const easeAppleSheet = cubicBezierEase(0.32, 0.72, 0, 1);

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpViewBox(
  from: ViewBoxLike,
  to: ViewBoxLike,
  t: number,
): ViewBoxLike {
  return {
    left: lerp(from.left, to.left, t),
    right: lerp(from.right, to.right, t),
    top: lerp(from.top, to.top, t),
    bottom: lerp(from.bottom, to.bottom, t),
  };
}

/**
 * Runs `apply(easedT)` from 0 to 1. Returns a cancel function that stops
 * further frames and does not call `onDone`.
 */
export function animateApple(
  durationMs: number,
  ease: (t: number) => number,
  apply: (eased: number) => void,
  onDone?: () => void,
): () => void {
  if (durationMs <= 0 || prefersReducedMotion()) {
    apply(1);
    onDone?.();
    return () => {};
  }

  let raf = 0;
  let cancelled = false;
  const start = performance.now();

  const tick = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / durationMs);
    apply(ease(t));
    if (t < 1) {
      raf = requestAnimationFrame(tick);
    } else {
      onDone?.();
    }
  };

  raf = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

/** Matches `right-4` so the ghost clears the viewport, not a 24px nudge. */
const SHEET_LEAVE_GAP_PX = 16;

const SHEET_LEAVE_STRIP_CLASSES = [
  "apple-sheet-float",
  "h-almost-full",
  "h-almost-full-agent",
  "top-almost-full",
  "with-transition",
  "apple-layout",
] as const;

/**
 * CSS enter on `.apple-sheet-float` cannot play a leave: `v-if` removes the
 * node immediately. Clone the painted sheet onto `body`, pin it to the live
 * rect, and slide it off in one translateX. Strip layout-transition classes
 * (`h-almost-full` / `top-almost-full` / `with-transition`) — those would
 * run as a second motion after the 24px nudge, which is the two-stage close.
 */
export function playAppleSheetLeave(el: HTMLElement): void {
  if (typeof document === "undefined" || !el.isConnected) return;
  const ms = appleDurationMs("sheet-out");
  if (ms <= 0) return;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;

  const clone = el.cloneNode(true) as HTMLElement;
  clone.removeAttribute("id");
  clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
  clone.classList.remove(...SHEET_LEAVE_STRIP_CLASSES);
  clone.classList.add(APPLE_SHEET_LEAVE_GHOST_CLASS);
  clone.setAttribute("aria-hidden", "true");
  const leavePx = rect.width + SHEET_LEAVE_GAP_PX;
  Object.assign(clone.style, {
    pointerEvents: "none",
    position: "fixed",
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    right: "auto",
    margin: "0",
    overflow: "hidden",
    transform: "translateX(0px)",
    transition: "none",
    animation: "none",
  });
  document.body.appendChild(clone);
  el.style.visibility = "hidden";

  animateApple(
    ms,
    easeAppleSheet,
    (t) => {
      clone.style.transform = `translateX(${t * leavePx}px)`;
    },
    () => clone.remove(),
  );
}

/** Put on a floating sheet root. Pass `false` to skip (docked split-view). */
export const vAppleSheetLeave: ObjectDirective<
  HTMLElement,
  boolean | undefined
> = {
  beforeUnmount(el, binding) {
    if (binding.value === false) return;
    playAppleSheetLeave(el);
  },
};

/**
 * CSS leave on `<Transition>` does not run when a parent `v-if` tears the
 * modal down (filters, protection, search). Clone onto `body` and reverse
 * the enter: backdrop blur/dim and panel scale/fade, with no opacity on an
 * ancestor of `backdrop-filter` (that would snap the blur off on frame 1).
 */
export function playAppleDialogLeave(el: HTMLElement): void {
  if (typeof document === "undefined" || !el.isConnected) return;
  if (el.style.visibility === "hidden") return;
  const ms = appleDurationMs("layout");
  if (ms <= 0) return;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;

  const clone = el.cloneNode(true) as HTMLElement;
  clone.removeAttribute("id");
  clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
  clone.classList.add(APPLE_DIALOG_LEAVE_GHOST_CLASS);
  clone.setAttribute("aria-hidden", "true");
  Object.assign(clone.style, {
    pointerEvents: "none",
    position: "fixed",
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    right: "auto",
    margin: "0",
    zIndex: "50",
    overflow: "hidden",
    transition: "none",
    animation: "none",
  });
  document.body.appendChild(clone);
  el.style.visibility = "hidden";

  const srcBackdrop = el.querySelector<HTMLElement>(".apple-dialog-backdrop");
  const backdrop = clone.querySelector<HTMLElement>(".apple-dialog-backdrop");
  const panel = clone.querySelector<HTMLElement>(".apple-dialog-panel");
  const dimmed = Boolean(
    srcBackdrop?.classList.contains("bg-black-opaced") ||
    backdrop?.classList.contains("bg-black-opaced"),
  );

  animateApple(
    ms,
    easeApple,
    (t) => {
      if (backdrop && dimmed) {
        const blur = (1 - t) * DIALOG_BACKDROP_BLUR_PX;
        const alpha = (1 - t) * DIALOG_BACKDROP_DIM_ALPHA;
        const filter = `blur(${blur}px)`;
        backdrop.style.backdropFilter = filter;
        // Safari still needs the prefix, which is absent from the DOM typings.
        backdrop.style.setProperty("-webkit-backdrop-filter", filter);
        backdrop.style.backgroundColor = `rgba(0, 0, 0, ${alpha})`;
      }
      if (panel) {
        // Fade the children, not the panel: opacity here would drop their
        // backdrop-filter on the first frame.
        for (const child of Array.from(panel.children) as HTMLElement[]) {
          child.style.opacity = String(1 - t);
        }
        panel.style.transform = `scale(${1 + 0.05 * t})`;
      }
    },
    () => clone.remove(),
  );
}

export type RectLike = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const FLIP_STYLE_KEYS = [
  "position",
  "top",
  "left",
  "width",
  "height",
  "right",
  "bottom",
  "margin",
  "zIndex",
  "overflow",
  "boxSizing",
  "maxWidth",
  "maxHeight",
  "transition",
  "animation",
] as const;

const inflightFlip = new WeakMap<HTMLElement, () => void>();

function clearFlipStyles(el: HTMLElement): void {
  el.classList.remove(APPLE_LAYOUT_FLIP_CLASS);
  for (const key of FLIP_STYLE_KEYS) el.style[key] = "";
}

/**
 * Shared-element morph after a layout jump (`position` in-flow → fixed, etc.).
 * Capture `first` before the class change, call this after the DOM has the
 * destination box, and the node interpolates top/left/width/height — not
 * transform, so child backdrop-filter stays alive.
 */
export function playAppleRectMorph(
  el: HTMLElement,
  first: RectLike,
): () => void {
  inflightFlip.get(el)?.();

  if (typeof document === "undefined" || !el.isConnected) return () => {};
  const ms = appleDurationMs("layout");
  if (ms <= 0) return () => {};
  if (first.width === 0 && first.height === 0) return () => {};

  const last = el.getBoundingClientRect();
  if (last.width === 0 && last.height === 0) return () => {};

  const from = {
    top: first.top,
    left: first.left,
    width: first.width,
    height: first.height,
  };
  const to = {
    top: last.top,
    left: last.left,
    width: last.width,
    height: last.height,
  };

  const sameBox =
    Math.abs(from.left - to.left) < 0.5 &&
    Math.abs(from.top - to.top) < 0.5 &&
    Math.abs(from.width - to.width) < 0.5 &&
    Math.abs(from.height - to.height) < 0.5;
  if (sameBox) return () => {};

  const destIsOverlay = el.classList.contains("table-fullscreen");
  const spacerSize = destIsOverlay ? from : to;
  let spacer: HTMLDivElement | null = null;
  if (el.parentElement) {
    spacer = document.createElement("div");
    spacer.setAttribute("aria-hidden", "true");
    spacer.style.width = `${spacerSize.width}px`;
    spacer.style.height = `${spacerSize.height}px`;
    spacer.style.flexShrink = "0";
    spacer.style.pointerEvents = "none";
    el.parentElement.insertBefore(spacer, el);
  }

  el.classList.add(APPLE_LAYOUT_FLIP_CLASS);
  Object.assign(el.style, {
    position: "fixed",
    top: `${from.top}px`,
    left: `${from.left}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    right: "auto",
    bottom: "auto",
    margin: "0",
    zIndex: "11",
    overflow: "hidden",
    boxSizing: "border-box",
    maxWidth: "none",
    maxHeight: "none",
    transition: "none",
    animation: "none",
  });

  let cleaned = false;
  let cancel: () => void = () => {};
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    clearFlipStyles(el);
    spacer?.remove();
    if (inflightFlip.get(el) === cancel) inflightFlip.delete(el);
  };

  const stop = animateApple(
    ms,
    easeApple,
    (t) => {
      el.style.top = `${lerp(from.top, to.top, t)}px`;
      el.style.left = `${lerp(from.left, to.left, t)}px`;
      el.style.width = `${lerp(from.width, to.width, t)}px`;
      el.style.height = `${lerp(from.height, to.height, t)}px`;
    },
    cleanup,
  );

  cancel = () => {
    stop();
    cleanup();
  };
  inflightFlip.set(el, cancel);
  return cancel;
}
